import { LoadingOverlay } from '@/components/core';
import Header from '@/components/shared/Header';
import { useMe } from '@/api-client/use-me';
import { useAuthStore } from '@/state/store/auth';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet } from 'react-router-dom';
import { useActiveSubscription } from '@/modules/subscription/hooks/use-active-subscription';

export const MainLayout = () => {
  const { isAuthenticated, setAuthLoading, userId } = useAuthStore();
  const { isSuccess, isLoading } = useMe(isAuthenticated);
  const queryClient = useQueryClient();
  const previousUserId = useRef<string | null>(null);

  // Warm and retain the current subscription for all authenticated routes.
  useActiveSubscription();

  useEffect(() => {
    setAuthLoading(isLoading);
    if (!isAuthenticated) return;
    if (!isSuccess) return;
    setAuthLoading(false);
  }, [isAuthenticated, isSuccess, setAuthLoading, isLoading]);

  // Drop cached per-account data whenever the authenticated user changes
  // (account switch). Without this, queries cached for the previous account
  // (organizations, profile, contracts, etc.) leak into the new session — which
  // is what sent a freshly-switched user into the old org → /org/unauthorized.
  // Covers every sign-in path (email, SSO, OIDC, MFA). The current account query
  // (`getAccount`) is preserved to avoid an unnecessary refetch/loader flash.
  useEffect(() => {
    const prev = previousUserId.current;
    if (userId && prev && prev !== userId) {
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'getAccount' });
    }
    previousUserId.current = userId;
  }, [userId, queryClient]);

  return (
    <div className="flex flex-col min-h-screen w-screen">
      <Header />
      <main className="flex-1 ">
        {
          // Show loading state while fetching user data
          isLoading ? (
            <div className={`min-h-screen bg-[hsl(var(--bg))]`}>
              <LoadingOverlay />
            </div>
          ) : (
            <Outlet />
          )
        }
      </main>
    </div>
  );
};
